@use '@/styles/tokens' as *;

/* The label chip and the pin as one column centred on the projected point (Figma 823:701655).
 * <MapOverlay> positions its box's top-left there, so the stack translates itself onto it — the
 * transform that used to live on `.marker` moves here, because the PIN must stay centred on the
 * Device's coordinates whether or not a chip sits above it.
 *
 * The 28px in the translate is half the 56px HIT BOX, which is what keeps the pin — not the column —
 * on the point. Ticket 09's fan-out sets `left`/`top` inline to move the whole stack before this
 * transform runs. */
.stack {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  transform: translate(-50%, calc(-100% + 28px));
}

/* Figma 823:701656 — an 11px name chip above the pin, `Foundation /White/white-100` at $radius-pill
 * (16px renders identically at this height).
 *
 * The stack's 4px gap sits between this chip and the 56px HIT BOX, which extends 8px above the 40px
 * drawn pin — so the gap alone would leave 12px of daylight where Figma specifies 4. Pulling back by
 * exactly that slop is what makes the DRAWN gap 4px, and what makes LABEL_CHIP.pinOffset in
 * src/lib/maps/labels.ts (half the pin plus this gap) describe the chip actually on screen. SCSS
 * cannot import TS, so the two sides name each other.
 *
 * 5px block padding is off the --space-* scale and written raw, as .chip in field-details-panel does.
 *
 * `pointer-events: none` so the chip can never swallow a click meant for the marker it names. */
.label {
  margin-bottom: -8px;
  padding: 5px var(--space-2);
  border-radius: $radius-pill;
  background: $color-white-100;
  font-size: $font-size-11;
  line-height: 1;
  color: var(--text-primary);
  white-space: nowrap;
  pointer-events: none;
}

/* Figma 948:567818 — a 40px circle centred on the Device's coordinates, with a 20px Engine glyph.
 *
 * Two numbers are deliberate. The 56px box is the HIT TARGET, larger than the drawn pin so a near miss
 * at small zoom hits the Device rather than the Boundary beneath (113586 §6c) — it is
 * MARKER_HIT_SIZE_PX in src/lib/maps/markers.ts; SCSS cannot import TS, so the two sides name each
 * other instead. `border-radius: 50%` makes the hit-tested target a 28px-radius CIRCLE, not a square,
 * which is the radius ticket 07's MARKER_HIT_RADIUS derives as MARKER_HIT_SIZE_PX / 2 — so the disc
 * this rule hit-tests and the disc `resolveMapClick` answers over are one and the same. Changing the
 * box here moves both.
 *
 * An in-flow item of `.stack` above — NOT positioned. Absolute here would pull the pin out of the
 * column and let it overlap the chip; the centring transform lives on `.stack`.
 *
 * There is no status variant, by design (113587 §6a/§6b). */
.marker {
  @include reset-button;
  /* 113588 §13e — a visible focus indicator as the Grower tabs the markers. The mixin emits its own
   * `&:focus-visible`, so it is included bare. */
  @include focus-ring;

  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  /* <MapOverlay>'s box takes no pointer events, so a pan drag over empty space still reaches the map;
   * the interactive pin opts back in. */
  pointer-events: auto;
}

/* 113586 §3c — a Device outside the focused Field. Depth only, no other treatment, so it can never be
 * mistaken for a Device Status (113587 §6b). No Figma frame draws this state. */
.muted {
  opacity: 0.5;
}

/* Figma 948:567819: the ellipse is a flat `Error/700` fill — no stroke, no shadow. The 43 × 38 outer
 * frame in Figma is loose group bounds, not a shadow box; do not reproduce it. */
.pin {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: $color-white-100;
  background: $color-error-700;
  border-radius: 50%;
}
